import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getLang, setLang } from "../i18n";

const API = process.env.REACT_APP_API_URL;

export default function PersonPage() {
  const { slug } = useParams();
  const [lang, _setLang] = useState(getLang());

  const [loading, setLoading] = useState(true);
  const [person, setPerson] = useState(null);
  const [contribs, setContribs] = useState([]);

  // Form
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [text, setText] = useState("");
  const [contentLang, setContentLang] = useState("kn"); // default Kannada
  const [files, setFiles] = useState([]);
  const [msg, setMsg] = useState("");

  const title = useMemo(() => {
    if (!person) return "";
    return lang === "kn"
      ? (person.name_kn || person.name_en)
      : (person.name_en || person.name_kn);
  }, [person, lang]);

  function switchLang() {
    const next = lang === "en" ? "kn" : "en";
    setLang(next);
    _setLang(next);
  }

  async function load() {
    setLoading(true);
    setMsg("");
    try {
      const res = await axios.get(`${API}/api/public/persons/${slug}`);
      setPerson(res.data.person);
      setContribs(res.data.contributions || []);
    } catch (e) {
      setPerson(null);
      setContribs([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [slug]);

  async function submitContribution(e) {
    e.preventDefault();
    setMsg("");

    if (!fullName.trim() || !text.trim()) {
      setMsg(lang === "kn" ? "ಹೆಸರು ಮತ್ತು ವಿವರಣೆ ಅಗತ್ಯ" : "Name and text are required");
      return;
    }

    try {
      // If there are files => multipart. Else JSON.
      if (files.length > 0) {
        const fd = new FormData();
        fd.append("full_name", fullName);
        fd.append("mobile", mobile);
        fd.append("contribution_text", text);
        fd.append("content_lang", contentLang);

        for (const f of files) fd.append("files", f);

        await axios.post(`${API}/api/public/persons/${slug}/contributions`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${API}/api/public/persons/${slug}/contributions`, {
          full_name: fullName,
          mobile,
          contribution_text: text,
          content_lang: contentLang,
        });
      }

      setFullName("");
      setMobile("");
      setText("");
      setFiles([]);
      setMsg(lang === "kn" ? "ಸಲ್ಲಿಸಲಾಗಿದೆ. ಆಡ್ಮಿನ್ ಅನುಮೋದನೆಗಾಗಿ ಕಾಯಿರಿ." : "Submitted. Awaiting admin approval.");

      // reload (approved list won’t show it until approved, but safe)
      load();
    } catch (e) {
      setMsg(e?.response?.data?.message || "Submit failed");
    }
  }

  if (loading) {
    return (
      <div style={{ fontFamily: "system-ui", padding: 24 }}>
        Loading...
      </div>
    );
  }

  if (!person) {
    return (
      <div style={{ fontFamily: "system-ui", padding: 24 }}>
        Person not found.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "system-ui", background: "#f5f7fb", minHeight: "100vh" }}>
      <div style={{ background: "#0d47a1", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between" }}>
        <div style={{ fontWeight: 800 }}>{title}</div>
        <button onClick={switchLang} style={{ background: "white", border: 0, padding: "6px 10px", borderRadius: 6, cursor: "pointer" }}>
          {lang === "en" ? "ಕನ್ನಡ" : "English"}
        </button>
      </div>

      <div style={{ maxWidth: 1000, margin: "18px auto", padding: "0 14px" }}>
        {/* Person card */}
        <div style={{ background: "white", borderRadius: 14, padding: 16, boxShadow: "0 6px 20px rgba(16,24,40,0.08)" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <div style={{ width: 88, height: 88, borderRadius: 14, overflow: "hidden", border: "1px solid #e7eaf0", background: "#fafafa" }}>
              {person.photo_url ? (
                <img src={`${API}${person.photo_url}`} alt="person" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : null}
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 900 }}>{title}</div>
              <div style={{ marginTop: 6, color: "#555" }}>
                {lang === "kn"
                  ? (person.short_intro_kn || person.short_intro_en)
                  : (person.short_intro_en || person.short_intro_kn) || ""}
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
                Share link: <span style={{ fontWeight: 700 }}>/p/{person.slug}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Submit form */}
        <div style={{ background: "white", borderRadius: 14, padding: 16, marginTop: 14, boxShadow: "0 6px 20px rgba(16,24,40,0.08)" }}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>
            {lang === "kn" ? "ಈ ವ್ಯಕ್ತಿಯ ಬಗ್ಗೆ ಮಾಹಿತಿ ಸೇರಿಸಿ" : "Add information about this person"}
          </div>

          <form onSubmit={submitContribution} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={lang === "kn" ? "ನಿಮ್ಮ ಹೆಸರು" : "Your name"}
              style={inp}
            />
            <input
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder={lang === "kn" ? "ಮೊಬೈಲ್ (ಐಚ್ಛಿಕ)" : "Mobile (optional)"}
              style={inp}
            />

            <select value={contentLang} onChange={(e) => setContentLang(e.target.value)} style={inp}>
              <option value="en">English</option>
              <option value="kn">Kannada</option>
              <option value="mixed">Mixed</option>
            </select>

            <input
              type="file"
              multiple
              accept="image/*,audio/*,video/*"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              style={inp}
            />

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={lang === "kn" ? "ಮಾಹಿತಿ ಬರೆಯಿರಿ (ಕನ್ನಡದಲ್ಲೂ ಬರೆಯಬಹುದು)" : "Write details (Kannada supported)"}
              style={{ ...inp, gridColumn: "1 / -1", minHeight: 110 }}
            />

            <button type="submit" style={{ gridColumn: "1 / -1", ...btn }}>
              {lang === "kn" ? "ಸಲ್ಲಿಸಿ" : "Submit"}
            </button>

            {msg ? (
              <div style={{ gridColumn: "1 / -1", color: "#0d47a1", fontWeight: 700 }}>{msg}</div>
            ) : null}
          </form>
        </div>

        {/* Approved contributions */}
        <div style={{ marginTop: 14, background: "white", borderRadius: 14, padding: 16, boxShadow: "0 6px 20px rgba(16,24,40,0.08)" }}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>
            {lang === "kn" ? "ಅನುಮೋದಿತ ಮಾಹಿತಿ" : "Approved Information"}
          </div>

          {contribs.length === 0 ? (
            <div style={{ color: "#666" }}>No approved contributions yet.</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {contribs.map((c) => (
                <div key={c.id} style={{ border: "1px solid #e7eaf0", borderRadius: 12, padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ fontWeight: 800 }}>{c.full_name}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  <div style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>{c.contribution_text}</div>

                  {Array.isArray(c.media) && c.media.length > 0 ? (
                    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                      {c.media.map((m) => (
                        <div key={m.id} style={{ border: "1px solid #e7eaf0", borderRadius: 10, overflow: "hidden" }}>
                          {m.media_type === "image" && (
                            <img src={`${API}${m.url}`} alt="media" style={{ width: "100%", height: 100, objectFit: "cover" }} />
                          )}
                          {m.media_type === "video" && (
                            <video controls style={{ width: "100%", height: 100, objectFit: "cover" }}>
                              <source src={`${API}${m.url}`} />
                            </video>
                          )}
                          {m.media_type === "audio" && (
                            <div style={{ padding: 8 }}>
                              <audio controls style={{ width: "100%" }}>
                                <source src={`${API}${m.url}`} />
                              </audio>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inp = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e7eaf0",
  outline: "none",
  fontSize: 14,
};

const btn = {
  padding: "10px 12px",
  borderRadius: 10,
  border: 0,
  background: "#0d47a1",
  color: "white",
  cursor: "pointer",
  fontWeight: 800,
};
